import React from 'react';
import styled from 'styled-components';
import { Field } from '@increasecard/typed-components/dist/types/Field';
import { ClockIcon } from '@increasecard/icons';
import {
  NumberBox,
  localizedFormatRelativeToNow,
  localizedTimestamp,
  NumberBoxProps
} from '@card/commonlib';
import { ContentCard, ContentWrapper } from '../../common/components/ContentCard';
import { CalendarSection } from './components/CalendarSection/CalendarSection';
import { useDashboardScreen } from './useDashboardScreen';
import { useTranslation } from 'react-i18next';
import { ENABLE_DASHBOARD_LAST_UPDATE } from '../../flags';
import { LoadableStatus, Loadable } from '@card/types';
import { CurrencyTabs } from '../../common/components/CurrencyTabs';

const TotalsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  grid-gap: 16px;
  padding: 8px;
  border: 1px solid ${({ theme }) => theme.colorsNew.product.regular};
  background: ${({ theme }) => theme.colorsNew.product.light};
  border-radius: 9px;
  margin-bottom: 16px;
`;

const LastUpdate = styled.div`
  text-align: right;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  margin-bottom: 24px;
  ${({ theme }) => theme.typography.cell}
  svg {
    color: ${({ theme }) => theme.colorsNew.gray.grayBold};
    margin-right: 4px;
  }
`;

interface LoadableNumberBoxProps extends Omit<NumberBoxProps, 'value'> {
  value: Loadable<number>;
}

function LoadableNumberBox({ value, ...rest }: LoadableNumberBoxProps) {
  return (
    <NumberBox value={value.status === LoadableStatus.Fetched ? value.data : undefined} {...rest} />
  );
}

export function DashboardScreen() {
  const { t } = useTranslation('dashboard', { useSuspense: true });
  const {
    monthAndYear,
    setMonthAndYear,
    collectedAmounts,
    soldAmounts,
    lastUpdate,
    filters,
    setFilters,
    filterFields,
    collectToday,
    collectTomorrow,
    monthlyChargebacks,
    currency,
    currencies,
    setCurrency
  } = useDashboardScreen();

  return (
    <ContentCard>
      {currencies && currencies.length > 1 && currency && (
        <CurrencyTabs currencies={currencies} onChange={setCurrency} value={currency} />
      )}
      <ContentWrapper>
        {ENABLE_DASHBOARD_LAST_UPDATE && (
          <LastUpdate title={localizedTimestamp(lastUpdate)}>
            <ClockIcon height="20" width="20" />{' '}
            {t('lastUpdate', { relativeTime: localizedFormatRelativeToNow(lastUpdate) })}
          </LastUpdate>
        )}
        <TotalsContainer>
          <LoadableNumberBox
            currency="ARS"
            title={t('toCollectToday')}
            value={collectToday}
            variant="dark30"
          />
          <LoadableNumberBox
            currency="ARS"
            title={t('toCollectTomorrow')}
            value={collectTomorrow}
            variant="dark30"
          />
          <LoadableNumberBox
            currency="ARS"
            title={t('monthlyChargebacks')}
            value={monthlyChargebacks}
            variant="white"
          />
        </TotalsContainer>
        <CalendarSection
          collectedAmounts={collectedAmounts}
          filterFields={filterFields as Field[]}
          filters={filters}
          monthAndYear={monthAndYear}
          onChangeMonthAndYear={setMonthAndYear}
          setFilters={setFilters}
          soldAmounts={soldAmounts}
        />
      </ContentWrapper>
    </ContentCard>
  );
}
